/**
 * API service for AI Agent frontend
 * Handles token-based authentication and domain validation
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.deepchat.se/api'

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

export interface DomainValidationResponse {
  isValid: boolean
  companyId?: string
  companyName?: string
  message?: string
}

export interface CompanyInfo {
  id: string
  name: string
  domain: string
  approvedDomains: string[]
}

class ApiService {
  private baseUrl: string

  constructor() {
    this.baseUrl = API_BASE_URL
  }

  /**
   * Validate domain and token
   */
  async validateDomain(token: string, domain: string): Promise<DomainValidationResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/validate-domain`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, domain }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Validation failed' }))
        return {
          isValid: false,
          message: error.error || error.message || 'Domain validation failed',
        }
      }

      const data = await response.json()
      return {
        isValid: data.success || data.isValid || false,
        companyId: data.data?.companyId || data.companyId,
        companyName: data.data?.companyName || data.companyName,
        message: data.message || data.data?.message,
      }
    } catch (error) {
      console.error('Domain validation error:', error)
      return {
        isValid: false,
        message: 'Failed to validate domain. Please check your connection.',
      }
    }
  }

  /**
   * Get company information by token
   */
  async getCompanyInfo(token: string): Promise<ApiResponse<CompanyInfo>> {
    try {
      const response = await fetch(`${this.baseUrl}/company/info`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to fetch company info' }))
        return {
          success: false,
          error: error.error || error.message || 'Failed to fetch company information',
        }
      }

      const data = await response.json()
      return {
        success: true,
        data: data.data || data,
      }
    } catch (error) {
      console.error('Get company info error:', error)
      return {
        success: false,
        error: 'Failed to fetch company information',
      }
    }
  }

  /**
   * Send chat message to AI agent with conversation history
   */
  async sendMessage(
    token: string,
    message: string,
    domain: string,
    agentId?: string,
    agentName?: string,
    conversationHistory?: Array<{ role: 'user' | 'assistant', content: string }>,
    visitorToken?: string,
    cookieId?: string
  ): Promise<ApiResponse<{ response: string; conversationId?: string; messageId?: string; offTopic?: boolean; strikes?: number; strikeLimit?: number; blocked?: boolean; unblockAt?: string }>> {
    try {
      const response = await fetch(`${this.baseUrl}/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          domain,
          agentId,
          agentName,
          conversationHistory: conversationHistory || [],
          visitorToken,
          cookieId
        }),
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        return {
          success: false,
          error: (data as any).message || (data as any).error || 'Failed to send message',
          data: {
            response: (data as any).message || '',
            blocked: (data as any).blocked,
            unblockAt: (data as any).unblockAt
          }
        }
      }

      return {
        success: true,
        data: (data as any).data
          ? { ...(data as any).data, offTopic: (data as any).offTopic, strikes: (data as any).strikes, strikeLimit: (data as any).strikeLimit }
          : { response: (data as any).response || (data as any).message || '' },
      }
    } catch (error) {
      console.error('Send message error:', error)
      return {
        success: false,
        error: 'Failed to send message. Please try again.',
      }
    }
  }

  /**
   * Generate a CAPTCHA image for visitor verification.
   */
  async getCaptcha(): Promise<ApiResponse<{ captchaId: string; imageDataUrl: string; expiresAt: string }>> {
    try {
      const response = await fetch(`${this.baseUrl}/visitor/captcha`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        return { success: false, error: (data as any).message || 'Failed to load captcha' }
      }
      return { success: true, data: (data as any).data }
    } catch (error) {
      console.error('Captcha error:', error)
      return { success: false, error: 'Failed to load captcha' }
    }
  }

  /**
   * Verify visitor with name + captcha and start session.
   */
  async startVisitorSession(
    token: string,
    payload: { name: string; captchaId: string; answer: string; cookieId: string; agentId?: string }
  ): Promise<ApiResponse<{ visitorToken: string; name: string }> & { blocked?: boolean; unblockAt?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/visitor/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        return {
          success: false,
          error: (data as any).message || 'Verification failed',
          blocked: (data as any).blocked,
          unblockAt: (data as any).unblockAt
        } as any
      }
      return { success: true, data: (data as any).data }
    } catch (error) {
      console.error('Visitor start error:', error)
      return { success: false, error: 'Verification failed' }
    }
  }

  /**
   * Check if a visitor (cookie/IP) is currently blocked.
   */
  async getVisitorStatus(
    token: string,
    cookieId: string
  ): Promise<ApiResponse<{ blocked: boolean; unblockAt: string | null; reason: string | null }>> {
    try {
      const response = await fetch(`${this.baseUrl}/visitor/status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cookieId })
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        return { success: false, error: (data as any).message || 'Status check failed' }
      }
      return { success: true, data: (data as any).data }
    } catch (error) {
      console.error('Visitor status error:', error)
      return { success: false, error: 'Status check failed' }
    }
  }

  /**
   * Submit feedback for a message (thumbs up/down)
   */
  async submitFeedback(
    messageId: string,
    conversationId: string,
    feedbackType: 'positive' | 'negative',
    comment?: string
  ): Promise<ApiResponse<void>> {
    const feedbackUrl = `${this.baseUrl}/feedback`
    console.log('Sending feedback to:', feedbackUrl)
    
    try {
      const body = {
        messageId,
        conversationId,
        feedbackType,
        comment
      }
      console.log('Feedback payload:', body)
      
      const response = await fetch(feedbackUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      console.log('Feedback response status:', response.status)
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to submit feedback' }))
        console.error('Feedback error response:', error)
        return {
          success: false,
          error: error.error || error.message || 'Failed to submit feedback',
        }
      }

      const data = await response.json()
      console.log('Feedback success response:', data)
      return { success: true }
    } catch (error) {
      console.error('Submit feedback error:', error)
      return {
        success: false,
        error: 'Failed to submit feedback',
      }
    }
  }
}

export const apiService = new ApiService()
